import { Link } from 'react-router-dom';
import { Card, Button } from '@/components/ui';
import {
  Clock,
  ArrowRight,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeftRight,
  ClipboardCheck,
  Package,
} from 'lucide-react';
import type { Mouvement } from '@/types/inventory.types';

interface RecentActivityWidgetProps {
  movements: Mouvement[];
}

function getMovementIcon(type: string) {
  switch (type) {
    case 'entrée':
      return <ArrowUpCircle className="text-green-500" size={16} />;
    case 'sortie':
      return <ArrowDownCircle className="text-red-500" size={16} />;
    case 'transfert':
      return <ArrowLeftRight className="text-blue-500" size={16} />;
    case 'ajustement':
    case 'inventaire':
      return <ClipboardCheck className="text-purple-500" size={16} />;
    default:
      return <Package className="text-gray-500 dark:text-gray-400" size={16} />;
  }
}

function getMovementLabel(type: string) {
  switch (type) {
    case 'entrée':
      return 'Entrée';
    case 'sortie':
      return 'Sortie';
    case 'transfert':
      return 'Transfert';
    case 'ajustement':
      return 'Ajustement';
    case 'inventaire':
      return 'Inventaire';
    default:
      return type;
  }
}

function timeAgo(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export function RecentActivityWidget({ movements }: RecentActivityWidgetProps) {
  return (
    <Card padding="md" className="border-l-4 border-l-blue-500">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
            <Clock className="text-blue-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
              Activité récente
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Derniers mouvements</p>
          </div>
        </div>
        <Link to="/history">
          <Button variant="ghost" size="sm" className="text-xs">
            Historique
            <ArrowRight size={14} className="ml-1" />
          </Button>
        </Link>
      </div>

      {movements.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <Clock size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Aucune activité récente</p>
        </div>
      ) : (
        <div className="space-y-2">
          {movements.map((movement) => (
            <div
              key={movement.id}
              className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
              <div className="flex-shrink-0">{getMovementIcon(movement.type)}</div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                  {movement.produits?.nom || 'Produit'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {getMovementLabel(movement.type)} • {movement.quantite} {movement.unite}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-gray-400">{timeAgo(movement.date)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
